import { useEffect, useState } from "react"
import { Icon } from "@/components/Icon"
import {
  downloadCaseDocument,
  fetchCaseDocument,
} from "@/features/file-review/services/document-download.service"

export interface ViewableFile {
  name: string
  url?: string
  label: string
}

interface DocViewerModalProps {
  file: ViewableFile
  onClose: () => void
}

export function DocViewerModal({ file, onClose }: DocViewerModalProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  useEffect(() => {
    if (!file.url) {
      setPreviewUrl(null)
      return
    }

    let active = true
    let objectUrl: string | null = null

    void fetchCaseDocument(file.url)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob)
        if (active) setPreviewUrl(objectUrl)
      })
      .catch(() => {
        if (active) setPreviewFailed(true)
      })

    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [file.url])

  const handleDownload = async () => {
    if (!file.url) return
    await downloadCaseDocument(file.url, file.name)
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <div
        className="docview"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={file.name}
      >
        <div className="docview__bar">
          <div className="fc-ico">
            <Icon name="file" size={16} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="nm">{file.name}</div>
            <div className="ty mono">{file.label}</div>
          </div>
          <div style={{ flex: 1 }} />
          {file.url && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => void handleDownload()}
            >
              <Icon name="download" size={14} /> Download
            </button>
          )}
          <button
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            style={{ position: "static" }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="docview__body" style={{ padding: 0 }}>
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title={file.name}
              style={{ width: "100%", height: "100%", border: "none", background: "#fff" }}
            />
          ) : (
            <div className="docview__ph" style={{ margin: 26 }}>
              <span>{previewFailed ? "Preview unavailable — download failed" : "Loading document preview..."}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
